import React, { useState, useEffect } from 'react';
import { ChevronLeft, Type, Palette, Sparkles, Languages, Copy, Check, Smile } from 'lucide-react'
import { catalogAPI } from '../../services/api';
import { injectFontFace, injectFontFamilyVariants } from '../../utils/fontLoader';

const TextSidebar = ({ isOpen, onClose, onTextSelect, onOpenDialog, isDialog = false, dialogSearchQuery = '', viewMode = 'grid' }) => {
  const [selectedTextType, setSelectedTextType] = useState('basic');
  const [copiedChar, setCopiedChar] = useState(null);

  // Language text options with default fonts
  const languageTextOptions = [
    { 
      id: 'english', 
      name: 'English', 
      fontSize: 36, 
      fontWeight: 'normal', 
      sample: 'English Text', 
      fontFamily: 'Bakbak One',
      description: 'Add English text'
    },
    { 
      id: 'hindi', 
      name: 'Hindi', 
      fontSize: 36, 
      fontWeight: 'normal', 
      sample: 'हिंदी पाठ', 
      fontFamily: 'Sarala',
      description: 'Add Hindi text'
    },
  ]
  
  // Load default fonts (Bakbak One and Sarala) when component mounts
  useEffect(() => {
    if (!isOpen) return
    
    const loadDefaultFonts = async () => {
      try {
        const response = await catalogAPI.getFonts()
        if (response.data?.success && response.data.data?.fonts) {
          const fonts = response.data.data.fonts
          
          // Load Bakbak One font
          const bakbakOneFont = fonts.find(f => f.family === 'Bakbak One')
          if (bakbakOneFont) {
            injectFontFamilyVariants(fonts, 'Bakbak One', 5)
          }
          
          // Load Sarala font
          const saralaFont = fonts.find(f => f.family === 'Sarala')
          if (saralaFont) {
            injectFontFamilyVariants(fonts, 'Sarala', 5)
          }
        }
      } catch (error) {
        console.warn('Failed to load default fonts:', error)
      }
    }
    
    loadDefaultFonts()
  }, [isOpen])

  // Hindi (Devanagari) characters organized by category
  const hindiCharacters = {
    vowels: {
      name: 'स्वर (Vowels)',
      chars: ['अ', 'आ', 'इ', 'ई', 'उ', 'ऊ', 'ऋ', 'ॠ', 'ऌ', 'ॡ', 'ए', 'ऐ', 'ओ', 'औ', 'अं', 'अः', 'ॲ', 'ॳ', 'ॴ', 'ॵ', 'ॶ', 'ॷ']
    },
    consonants: {
      name: 'व्यंजन (Consonants)',
      chars: [
        'क', 'ख', 'ग', 'घ', 'ङ',
        'च', 'छ', 'ज', 'झ', 'ञ',
        'ट', 'ठ', 'ड', 'ढ', 'ण',
        'त', 'थ', 'द', 'ध', 'न',
        'प', 'फ', 'ब', 'भ', 'म',
        'य', 'र', 'ल', 'व', 'श',
        'ष', 'स', 'ह', 'क्ष', 'त्र',
        'ज्ञ', 'श्र', 'क़', 'ख़', 'ग़',
        'ज़', 'ड़', 'ढ़', 'फ़', 'य़',
        'ऱ', 'ऴ', 'ळ', 'ऩ', 'ऽ'
      ]
    },
    matras: {
      name: 'मात्राएं (Vowel Signs)',
      chars: ['ा', 'ि', 'ी', 'ु', 'ू', 'ृ', 'ॄ', 'ॢ', 'ॣ', 'े', 'ै', 'ो', 'ौ', 'ं', 'ः', '्', 'ॅ', 'ॉ', 'ॊ', 'ो', '॑', '॒', '॓', '॔']
    },
    numbers: {
      name: 'संख्याएं (Numbers)',
      chars: ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९']
    },
    symbols: {
      name: 'चिह्न (Symbols)',
      chars: ['।', '॥', 'ॐ', '॰', '₹', '॓', '॔', '॑', '॒', '॰', 'ॱ', '॒', '॑', 'ॽ', 'ॾ', 'ॿ', '꣠', '꣡', '꣢', '꣣', '꣤', '꣥', '꣦', '꣧', '꣨', '꣩', '꣪', '꣫', '꣬', '꣭', '꣮', '꣯', '꣰', '꣱']
    },
    conjuncts: {
      name: 'संयुक्त अक्षर (Common Conjuncts)',
      chars: [
        'क्क', 'क्त', 'क्र', 'क्ल', 'क्व', 'क्ष', 'ख्र', 'ग्र', 'घ्र', 'ङ्क',
        'च्च', 'च्छ', 'च्य', 'छ्र', 'ज्ज', 'ज्झ', 'ज्ञ', 'ज्र', 'झ्र', 'ञ्च',
        'ट्ट', 'ट्ठ', 'ट्र', 'ठ्र', 'ड्ड', 'ड्ढ', 'ड्र', 'ढ्र', 'ण्ट', 'ण्ठ',
        'त्त', 'त्थ', 'त्न', 'त्म', 'त्र', 'त्व', 'थ्र', 'द्द', 'द्ध', 'द्र',
        'द्व', 'ध्र', 'न्त', 'न्द', 'न्न', 'न्म', 'न्य', 'न्र', 'न्व', 'प्त',
        'प्र', 'फ्र', 'ब्ज', 'ब्द', 'ब्ध', 'ब्र', 'भ्र', 'म्न', 'म्प', 'म्र',
        'य्र', 'र्क', 'र्ख', 'र्ग', 'र्घ', 'र्च', 'र्छ', 'र्ज', 'र्झ', 'र्ट',
        'र्ठ', 'र्ड', 'र्ढ', 'र्त', 'र्थ', 'र्द', 'र्ध', 'र्न', 'र्प', 'र्फ',
        'र्ब', 'र्भ', 'र्म', 'र्य', 'र्ल', 'र्व', 'र्श', 'र्ष', 'र्स', 'र्ह',
        'ल्क', 'ल्ग', 'ल्ट', 'ल्ड', 'ल्त', 'ल्द', 'ल्न', 'ल्प', 'ल्ब', 'ल्म',
        'ल्य', 'ल्र', 'ल्ल', 'ल्व', 'ल्ह', 'व्र', 'श्च', 'श्न', 'श्र', 'श्ल',
        'श्व', 'ष्क', 'ष्ट', 'ष्ठ', 'ष्ण', 'ष्प', 'ष्फ', 'ष्म', 'ष्य', 'ष्र',
        'ष्व', 'स्क', 'स्ख', 'स्त', 'स्थ', 'स्न', 'स्प', 'स्फ', 'स्म', 'स्य',
        'स्र', 'स्व', 'ह्ण', 'ह्न', 'ह्म', 'ह्य', 'ह्र', 'ह्ल', 'ह्व'
      ]
    }
  };

  // Comprehensive emoji collection organized by category
  const emojiCollection = {
    faces: {
      name: '😀 Faces & Emotions',
      chars: [
        '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
        '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '☺️', '😚',
        '😙', '🥲', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭',
        '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄',
        '😬', '🤥', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢',
        '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '🥸',
        '😎', '🤓', '🧐', '😕', '😟', '🙁', '☹️', '😮', '😯', '😲',
        '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱',
        '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠',
        '🤬', '😈', '👿', '💀', '☠️', '💩', '🤡', '👹', '👺', '👻'
      ]
    },
    gestures: {
      name: '👋 Gestures & People',
      chars: [
        '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞',
        '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍',
        '👎', '👊', '✊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝',
        '🙏', '✍️', '💅', '🤳', '💪', '🦾', '🦿', '🦵', '🦶', '👂',
        '🦻', '👃', '🧠', '🫀', '🫁', '🦷', '🦴', '👀', '👁️', '👅',
        '👄', '💋', '🩸', '👶', '🧒', '👦', '👧', '🧑', '👱', '👨',
        '🧔', '👩', '🧓', '👴', '👵', '🙍', '🙎', '🙅', '🙆', '💁',
        '🙋', '🧏', '🙇', '🤦', '🤷', '👮', '🕵️', '💂', '🥷', '👷'
      ]
    },
    activities: {
      name: '⚽ Activities & Sports',
      chars: [
        '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱',
        '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳',
        '🪁', '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛼', '🛷',
        '⛸️', '🥌', '🎿', '⛷️', '🏂', '🪂', '🏋️', '🤼', '🤸', '⛹️',
        '🤺', '🤾', '🏌️', '🏇', '🧘', '🏄', '🏊', '🤽', '🚣', '🧗',
        '🚵', '🚴', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️', '🏵️', '🎗️'
      ]
    },
    nature: {
      name: '🌱 Nature & Animals',
      chars: [
        '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
        '🦁', '🐮', '🐷', '🐽', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒',
        '🐔', '🐧', '🐦', '🐤', '🐣', '🐥', '🦆', '🦅', '🦉', '🦇',
        '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜',
        '🦟', '🦗', '🕷️', '🕸️', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕',
        '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳',
        '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🦧', '🐘', '🦛',
        '🦏', '🐪', '🐫', '🦒', '🦘', '🐃', '🐂', '🐄', '🐎', '🐖',
        '🐏', '🐑', '🦙', '🐐', '🦌', '🐕', '🐩', '🦮', '🐕‍🦺', '🐈',
        '🐓', '🦃', '🦚', '🦜', '🦢', '🦩', '🕊️', '🐇', '🦝', '🦨'
      ]
    },
    food: {
      name: '🍎 Food & Drinks',
      chars: [
        '🍎', '🍏', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈',
        '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦',
        '🥬', '🥒', '🌶️', '🫑', '🌽', '🥕', '🫒', '🧄', '🧅', '🥔',
        '🍠', '🥐', '🥖', '🫓', '🥨', '🥯', '🍞', '🧀', '🥚', '🍳',
        '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔',
        '🍟', '🍕', '🫔', '🌮', '🌯', '🫔', '🥙', '🧆', '🥚', '🍳',
        '🥘', '🍲', '🫕', '🥗', '🍿', '🧈', '🧂', '🥫', '🍱', '🍘',
        '🍙', '🍚', '🍛', '🍜', '🍝', '🍠', '🍢', '🍣', '🍤', '🍥',
        '🥮', '🍡', '🥟', '🥠', '🥡', '🦀', '🦞', '🦐', '🦑', '🦪'
      ]
    },
    travel: {
      name: '🚗 Travel & Places',
      chars: [
        '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐',
        '🛻', '🚚', '🚛', '🚜', '🏍️', '🛵', '🚲', '🛴', '🛹', '🛼',
        '🚁', '🛸', '✈️', '🛩️', '🛫', '🛬', '🪂', '💺', '🚀', '🛰️',
        '🚢', '⛵', '🚤', '🛥️', '🛳️', '⛴️', '🚂', '🚃', '🚄', '🚅',
        '🚆', '🚇', '🚈', '🚉', '🚊', '🚝', '🚞', '🚋', '🚌', '🚍',
        '🚎', '🚐', '🚑', '🚒', '🚓', '🚔', '🚕', '🚖', '🚗', '🚘',
        '🏠', '🏡', '🏘️', '🏚️', '🏗️', '🏭', '🏢', '🏬', '🏣', '🏤',
        '🏥', '🏦', '🏨', '🏪', '🏫', '🏩', '💒', '🏛️', '⛪', '🕌',
        '🛕', '🕍', '⛩️', '🕋', '⛲', '⛺', '🌁', '🌃', '🏙️', '🌄'
      ]
    },
    objects: {
      name: '📱 Objects & Symbols',
      chars: [
        '📱', '💻', '🖥️', '🖨️', '⌨️', '🖱️', '🖲️', '💽', '💾', '💿',
        '📀', '📼', '📷', '📸', '📹', '🎥', '📽️', '🎞️', '📞', '☎️',
        '📟', '📠', '📺', '📻', '🎙️', '🎚️', '🎛️', '🧭', '⏱️', '⏲️',
        '⏰', '🕰️', '⌛', '⏳', '📡', '🔋', '🔌', '💡', '🔦', '🕯️',
        '🪔', '🧯', '🛢️', '💸', '💵', '💴', '💶', '💷', '💰', '💳',
        '💎', '⚖️', '🧰', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🔩', '⚙️',
        '🧱', '⛓️', '🧲', '🔫', '💣', '🧨', '🪓', '🔪', '🗡️', '⚔️',
        '🛡️', '🚬', '⚰️', '🪦', '⚱️', '🏺', '🔮', '📿', '🧿', '💈'
      ]
    },
    symbols: {
      name: '❤️ Hearts & Symbols',
      chars: [
        '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
        '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️',
        '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐',
        '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐',
        '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳',
        '🈶', '🈚', '🈸', '🈺', '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️',
        '㊗️', '🈴', '🈵', '🈹', '🈲', '🅰️', '🅱️', '🆎', '🆑', '🅾️',
        '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫', '💯', '💢', '♨️',
        '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❕', '❓'
      ]
    }
  };

  // WordArt styles from backend
  const [wordArtStyles, setWordArtStyles] = useState([])
  useEffect(() => {
    if (isOpen && selectedTextType === 'wordart') {
      catalogAPI.getWordArt()
        .then(response => {
          const d = response.data
          if (d?.success) setWordArtStyles(d.data.wordart || [])
        })
        .catch(() => {})
    }
  }, [isOpen, selectedTextType])

  // 3D styles from backend
  const [wordArt3D, setWordArt3D] = useState([])
  useEffect(() => {
    if (isOpen && selectedTextType === '3d') {
      catalogAPI.getWordArt3D()
        .then(response => {
          const d = response.data
          if (d?.success) setWordArt3D(d.data.wordart3d || [])
        })
        .catch(() => {})
    }
  }, [isOpen, selectedTextType])

  const handleTextSelect = (textOption) => {
    if (textOption.type === 'basic' || textOption.type === 'language') {
      onTextSelect({
        type: 'text',
        content: textOption.sample,
        fontSize: textOption.fontSize,
        fontWeight: textOption.fontWeight,
        fontFamily: textOption.fontFamily || 'Arial', // Use specified font or fallback
        color: '#000000',
      });
    } else {
      onTextSelect({
        type: 'wordart',
        content: textOption.sample,
        style: textOption.style,
        wordArtId: textOption.id,
      });
    }
  };

  // Copy character to clipboard
  const copyToClipboard = async (char) => {
    try {
      await navigator.clipboard.writeText(char);
      setCopiedChar(char);
      setTimeout(() => setCopiedChar(null), 2000); // Clear after 2 seconds
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = char;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedChar(char);
      setTimeout(() => setCopiedChar(null), 2000);
    }
  };

  if (!isOpen && !isDialog) return null;

  return (
    <div className={isDialog 
      ? "h-full w-full bg-white flex flex-col" 
      : "fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-500 flex flex-col"
    }>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 
            className="text-lg font-semibold cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => onOpenDialog && onOpenDialog()}
            title="Click to open in large dialog"
          >
            Text
          </h2>
        </div>
      </div>

      {/* Text Type Tabs */}
      <div className="flex border-b border-gray-200 flex-shrink-0 overflow-x-auto">
        <button
          onClick={() => setSelectedTextType('basic')}
          className={`flex-shrink-0 px-3 py-3 text-sm font-medium ${
            selectedTextType === 'basic'
              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Type className="w-4 h-4 inline mr-1" />
          Basic
        </button>
        <button
          onClick={() => setSelectedTextType('hindi')}
          className={`flex-shrink-0 px-3 py-3 text-sm font-medium ${
            selectedTextType === 'hindi'
              ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Languages className="w-4 h-4 inline mr-1" />
          हिंदी
        </button>
        <button
          onClick={() => setSelectedTextType('emoji')}
          className={`flex-shrink-0 px-3 py-3 text-sm font-medium ${
            selectedTextType === 'emoji'
              ? 'text-yellow-600 border-b-2 border-yellow-600 bg-yellow-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Smile className="w-4 h-4 inline mr-1" />
          😀
        </button>
        <button
          onClick={() => setSelectedTextType('wordart')}
          className={`flex-shrink-0 px-3 py-3 text-sm font-medium ${
            selectedTextType === 'wordart'
              ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Sparkles className="w-4 h-4 inline mr-1" />
          WordArt
        </button>
        <button
          onClick={() => setSelectedTextType('3d')}
          className={`flex-shrink-0 px-3 py-3 text-sm font-medium ${
            selectedTextType === '3d'
              ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          3D
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {selectedTextType === 'basic' ? (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Click to add text</h3>
            {languageTextOptions.map((option) => (
              <div
                key={option.id}
                onClick={() => handleTextSelect({ ...option, type: 'language' })}
                className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                <div
                  style={{
                    fontSize: `${Math.min(option.fontSize, 28)}px`,
                    fontWeight: option.fontWeight,
                    fontFamily: `"${option.fontFamily}", sans-serif`,
                  }}
                  className="text-gray-800 mb-1"
                >
                  {option.sample}
                </div>
                <p className="text-sm text-gray-500">{option.description || option.name}</p>
              </div>
            ))}
          </div>
        ) : selectedTextType === 'hindi' ? (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">हिंदी अक्षर (Hindi Characters)</h3>
              <p className="text-xs text-gray-500">Click any character to copy to clipboard</p>
            </div>

            {Object.entries(hindiCharacters).map(([key, category]) => (
              <div key={key} className="mb-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                  {category.name}
                </h4>
                <div className="grid grid-cols-5 gap-2">
                  {category.chars.map((char, index) => (
                    <button
                      key={`${key}-${index}`}
                      onClick={() => copyToClipboard(char)}
                      className={`
                        relative p-3 border border-gray-200 rounded-lg text-center cursor-pointer
                        hover:border-orange-300 hover:bg-orange-50 transition-all duration-200
                        text-lg font-medium text-gray-800 hover:text-orange-700
                        ${copiedChar === char ? 'bg-green-100 border-green-300' : ''}
                      `}
                      title={`Copy "${char}" to clipboard`}
                    >
                      <span className="block">{char}</span>
                      {copiedChar === char && (
                        <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1">
                          <Check className="w-3 h-3" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start space-x-2">
                <Copy className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">How to use:</p>
                  <ul className="text-xs space-y-1 text-blue-700">
                    <li>• Click any character to copy it to your clipboard</li>
                    <li>• Paste it anywhere in your design using Ctrl+V</li>
                    <li>• Green checkmark shows successful copy</li>
                    <li>• Use conjuncts for complex Hindi text</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : selectedTextType === 'emoji' ? (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">😀 Emojis Collection</h3>
              <p className="text-xs text-gray-500">Click any emoji to copy to clipboard</p>
            </div>

            {Object.entries(emojiCollection).map(([key, category]) => (
              <div key={key} className="mb-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                  {category.name}
                </h4>
                <div className="grid grid-cols-5 gap-2">
                  {category.chars.map((emoji, index) => (
                    <button
                      key={`${key}-${index}`}
                      onClick={() => copyToClipboard(emoji)}
                      className={`
                        relative p-3 border border-gray-200 rounded-lg text-center cursor-pointer
                        hover:border-yellow-300 hover:bg-yellow-50 transition-all duration-200
                        text-lg hover:scale-110 transform
                        ${copiedChar === emoji ? 'bg-green-100 border-green-300' : ''}
                      `}
                      title={`Copy "${emoji}" to clipboard`}
                    >
                      <span className="block text-xl">{emoji}</span>
                      {copiedChar === emoji && (
                        <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1">
                          <Check className="w-3 h-3" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-start space-x-2">
                <Smile className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Emoji Tips:</p>
                  <ul className="text-xs space-y-1 text-yellow-700">
                    <li>• Click any emoji to copy it instantly</li>
                    <li>• Use emojis to make your designs more expressive</li>
                    <li>• Combine emojis with text for creative effects</li>
                    <li>• All emojis work in videos, images, and PDFs</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ) : selectedTextType === 'wordart' ? (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Choose a WordArt style</h3>
            <div className="grid grid-cols-1 gap-4">
              {wordArtStyles.map((wordArt) => (
                <div
                  key={wordArt.id}
                  onClick={() => handleTextSelect({ ...wordArt, type: 'wordart' })}
                  className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-purple-300 hover:bg-purple-50 transition-colors"
                >
                  <div className="mb-2 text-center" style={{ fontSize: '20px', fontWeight: wordArt?.style?.fontWeight || 'bold', color: wordArt?.style?.fill || '#000' }}>
                    {wordArt.sample || 'WordArt'}
                  </div>
                  <p className="text-sm text-gray-500 text-center">{wordArt.name}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Choose a 3D style</h3>
            <div className="grid grid-cols-1 gap-4">
              {wordArt3D.map((wa) => (
                <div
                  key={wa.id}
                  onClick={() => handleTextSelect({ ...wa, type: 'wordart' })}
                  className="p-4 border border-gray-200 rounded-lg cursor-pointer hover:border-emerald-300 hover:bg-emerald-50 transition-colors"
                >
                  <div className="mb-2 text-center" style={{ fontSize: '22px', fontWeight: wa?.style?.fontWeight || 'bold', color: wa?.style?.fill || '#000' }}>
                    {wa.sample || '3D Text'}
                  </div>
                  <p className="text-sm text-gray-500 text-center">{wa.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TextSidebar;
